/**
 * 根据用户角色过滤路由
 */
export function filterRoutesByRole(routes, userRoles) {
  if (!userRoles || userRoles.length === 0) {
    console.log('⚠️ 用户角色为空，返回空路由')
    return []
  }
  
  const normalizedUserRoles = userRoles.map(role => {
    if (typeof role === 'string' && role.trim() === '0') {
      return 0
    }
    const num = Number(role)
    return isNaN(num) ? role : num
  })
  
  console.log(`🔍 过滤路由 - 用户角色: ${normalizedUserRoles.join(',')}`)
  
  const filteredRoutes = routes.filter(route => {
    return hasPermission(route, normalizedUserRoles)
  }).map(route => {
    const clonedRoute = { ...route }
    
    if (clonedRoute.children && clonedRoute.children.length > 0) {
      clonedRoute.children = filterRoutesByRole(clonedRoute.children, userRoles)
    }
    
    return clonedRoute
  })
  
  console.log(`✅ 路由过滤完成，共 ${filteredRoutes.length} 个路由`)
  return filteredRoutes
}

/**
 * 检查路由是否有权限
 */
function hasPermission(route, userRoles) {
  if (!route.meta || !route.meta.roles || route.meta.roles.length === 0) {
    return true
  }
  
  const routeRoles = route.meta.roles.map(role => {
    if (typeof role === 'string' && role.trim() === '0') {
      return 0
    }
    const num = Number(role)
    return isNaN(num) ? role : num
  })
  
  const hasAccess = userRoles.some(userRole => 
    routeRoles.some(routeRole => {
      if (typeof userRole === 'number' && typeof routeRole === 'number') {
        return userRole === routeRole
      }
      return String(userRole) === String(routeRole)
    })
  )
  
  if (!hasAccess) {
    console.log(`⛔ 路由 ${route.path} 权限检查失败: 用户角色 ${userRoles.join(',')} 不在允许的角色 ${routeRoles.join(',')} 中`)
  }
  
  return hasAccess
}

// 在 permission.js 或相关文件中更新组件解析逻辑

/**
 * 解析路由组件
 */
export function resolveRouteComponents(routes, componentMap) {
  if (!routes || routes.length === 0) {
    return []
  }
  
  console.log(`🔧 解析路由组件，共 ${routes.length} 个路由`)
  
  return routes.map(route => {
    const resolvedRoute = { ...route }
    
    // 处理组件
    if (typeof resolvedRoute.component === 'string') {
      const componentKey = resolvedRoute.component
      
      // 尝试多种方式获取组件
      let component = null
      
      // 方式1：直接通过 key 查找
      if (componentMap[componentKey]) {
        component = componentMap[componentKey]
      }
      // 方式2：尝试通过路由 name 查找
      else if (resolvedRoute.name && componentMap[resolvedRoute.name]) {
        component = componentMap[resolvedRoute.name]
      }
      // 方式3：尝试动态导入
      else {
        console.warn(`⚠️ 找不到组件: ${componentKey}，使用默认404页面`)
        component = () => import('@/views/error-page/404')
      }
      
      resolvedRoute.component = component
    }
    
    // 递归处理子路由
    if (resolvedRoute.children && resolvedRoute.children.length > 0) {
      resolvedRoute.children = resolveRouteComponents(resolvedRoute.children, componentMap)
    }
    
    return resolvedRoute
  })
}

/**
 * 从路由生成菜单
 */
export function generateMenusFromRoutes(routes, parentPath = '') {
  if (!routes || routes.length === 0) {
    return []
  }
  
  const menus = []
  
  routes.forEach(route => {
    const currentPath = parentPath 
      ? `${parentPath}/${route.path}`.replace(/\/{2,}/g, '/')
      : route.path

    const visibleChildren = (route.children || []).filter(c => !c.hidden)

    // 如果父级隐藏但子级可见，则将子级提升到当前层级，保障二级菜单显示
    if (route.hidden) {
      const promoted = generateMenusFromRoutes(visibleChildren, currentPath)
      menus.push(...promoted)
      return
    }
    
    const menuItem = {
      name: route.name,
      path: currentPath,
      meta: { ...route.meta },
      children: generateMenusFromRoutes(visibleChildren, currentPath)
    }
    
    const alwaysShow = !!(route.meta && route.meta.alwaysShow)
    if (alwaysShow || menuItem.children.length > 0 || visibleChildren.length === 0) {
      menus.push(menuItem)
    }
  })
  
  return menus
}

/**
 * 扁平化路由
 */
export function flattenRoutes(routes) {
  const flattened = []
  
  function flatten(routeList, parentPath = '') {
    routeList.forEach(route => {
      const fullPath = parentPath ? `${parentPath}/${route.path}`.replace(/\/\//g, '/') : route.path
      
      const flatRoute = {
        ...route,
        fullPath
      }
      
      flattened.push(flatRoute)
      
      if (route.children && route.children.length > 0) {
        flatten(route.children, fullPath)
      }
    })
  }
  
  flatten(routes)
  return flattened
}

/**
 * 检查路由是否在列表中
 */
export function isRouteInList(path, routeList) {
  return routeList.some(route => route.path === path)
}

/**
 * 获取所有路由路径
 */
export function getAllRoutePaths(routes) {
  const paths = []
  
  function collectPaths(routeList) {
    routeList.forEach(route => {
      if (route.path && route.path !== '*' && route.path !== '/404') {
        paths.push(route.path)
      }
      if (route.children) {
        collectPaths(route.children)
      }
    })
  }
  
  collectPaths(routes)
  return [...new Set(paths)]
}
